import type { WidgetId } from '@wix/members-area-app-definitions';
import type { EditorSDK } from '@wix/platform-editor-sdk';

import type { RouteConfiguration } from '../../../../types/controllers';
import { updateRoutePathInMenuItems } from '../../../wrappers/menus';
import { getAppData, setAppData } from './app-data';

const SETTINGS_ROUTES_KEY = 'settingsRoutes';

type SettingsRoutesKey = typeof SETTINGS_ROUTES_KEY;

const PROFILE_ROUTES_KEY = 'routes';

type ProfileRoutesKey = typeof PROFILE_ROUTES_KEY;

const STANDALONE_MEMBER_PAGES_ROUTES_KEY = 'standaloneMemberPagesRoutes';

type StandaloneMemberPagesRoutesKey = typeof STANDALONE_MEMBER_PAGES_ROUTES_KEY;

export const getMemberSettingsPageRoutes = async (editorSDK: EditorSDK) => {
  const routes = await getAppData<SettingsRoutesKey, RouteConfiguration[]>(editorSDK, SETTINGS_ROUTES_KEY);
  return routes ?? [];
};

export const setMemberSettingsPageRoutes = async (editorSDK: EditorSDK, routes: RouteConfiguration[]) => {
  await setAppData<SettingsRoutesKey, RouteConfiguration[]>(editorSDK, SETTINGS_ROUTES_KEY, routes);
};

export const setStandaloneMemberPagesRoutes = async (editorSDK: EditorSDK, routes: RouteConfiguration[]) => {
  await setAppData<StandaloneMemberPagesRoutesKey, RouteConfiguration[]>(
    editorSDK,
    STANDALONE_MEMBER_PAGES_ROUTES_KEY,
    routes,
  );
};

export const getProfilePageRoutes = async (editorSDK: EditorSDK) => {
  const routes = await getAppData<ProfileRoutesKey, RouteConfiguration[]>(editorSDK, PROFILE_ROUTES_KEY);
  return routes ?? [];
};

export const setProfilePageRoutes = async (editorSDK: EditorSDK, routes: RouteConfiguration[]) => {
  await setAppData<ProfileRoutesKey, RouteConfiguration[]>(editorSDK, PROFILE_ROUTES_KEY, routes);
};

export const updateProfilePageRoute = async (
  editorSDK: EditorSDK,
  routeToUpdate: Partial<RouteConfiguration> & { widgetId: WidgetId },
) => {
  const routes = await getProfilePageRoutes(editorSDK);
  const updatedRoutes = routes.map((route) => {
    if (route.widgetId === routeToUpdate.widgetId) {
      return { ...route, ...routeToUpdate };
    }

    return route;
  });

  await setProfilePageRoutes(editorSDK, updatedRoutes);
};

export const updateRoutePath = async (editorSDK: EditorSDK, widgetId: WidgetId, newPath: string) => {
  if (!newPath) {
    throw new Error('Path must not be empty');
  }
  if (!/^[a-zA-Z0-9\-_]+$/.test(newPath)) {
    throw new Error('Path must contain only alphanumeric characters, hyphens, or underscores');
  }

  const routes = await getProfilePageRoutes(editorSDK);
  const currentRoute = routes.find((r) => r.widgetId === widgetId);
  const oldPath = currentRoute?.path;

  const isDuplicate = !!routes.find((r) => r.path === newPath && r.widgetId !== widgetId);
  if (isDuplicate) {
    throw new Error(`Path "${newPath}" is already used by another route`);
  }

  await updateProfilePageRoute(editorSDK, { widgetId, path: newPath });

  if (oldPath && oldPath !== newPath) {
    await updateRoutePathInMenuItems({ editorSDK, widgetId, oldPath, newPath });
  }
};
